import { createMenuAim } from '@theme/menu-aim';

/**
 * @param {HTMLElement} link
 * @returns {boolean}
 */
function isLinkKeyboardFocused(link) {
  if (!(link instanceof HTMLElement)) return false;

  try {
    if (CSS.supports('selector(:focus-visible)')) {
      return link.matches(':focus-visible');
    }
  } catch {
    // ignore
  }

  return link.matches(':focus');
}

/**
 * @param {HTMLElement} container
 */
function initMegaMenuThreeColumn(container) {
  if (!container || container.dataset.megaMenuThreeColumnInit === 'true') return;
  container.dataset.megaMenuThreeColumnInit = 'true';

  try {
    const mqDesktop = window.matchMedia('(min-width: 990px)');
    const mqHover = window.matchMedia('(hover: hover) and (pointer: fine)');

    const level1Links = container.querySelectorAll('.mega-menu__column--level-1 a.menu-level-1-link');
    const level2Column = container.querySelector('.mega-menu__column--level-2');
    const level2ColumnContent = level2Column?.querySelector('.mega-menu__column-content');
    const level2ColumnHeader = level2Column?.querySelector('.mega-menu__column-header');
    const level3Column = container.querySelector('.mega-menu__column--level-3');
    const level3ColumnContent = level3Column?.querySelector('.mega-menu__column-content');
    const level3ColumnHeader = level3Column?.querySelector('.mega-menu__column-header');

    if (!level1Links.length || !level2Column || !level3Column || !level2ColumnContent || !level3ColumnContent) return;

    const menuAim = createMenuAim(
      (targetEl) => {
        if (!targetEl) return null;
        const isLevel3 = targetEl.dataset?.aimTarget === 'level3';
        if (isLevel3) {
          const visible = getComputedStyle(level3Column).display !== 'none';
          if (visible) return level3Column.getBoundingClientRect();
          const l2 = level2Column.getBoundingClientRect();
          const width = Math.max(240, l2.width || 320);
          return new DOMRect(l2.right, l2.top, width, Math.max(1, l2.bottom - l2.top));
        }
        const l2Visible = getComputedStyle(level2Column).display !== 'none';
        if (l2Visible) return level2Column.getBoundingClientRect();
        const linkRect = targetEl.getBoundingClientRect();
        const cont = container.getBoundingClientRect();
        const width = Math.max(240, (cont.width * 0.25) | 0);
        return new DOMRect(linkRect.right, cont.top, width, Math.max(1, cont.bottom - cont.top));
      },
      { openDelayTowards: 300, closeDelay: 250 }
    );

    let activationTimer = null;
    let activationTimerId = 0;
    let closeTimer = null;

    function startTrackingIfDesktop() {
      if (mqDesktop.matches && mqHover.matches) menuAim.start();
    }
    function stopTracking() {
      menuAim.stop();
    }

    function hideAllL2Panels() {
      level2ColumnContent.querySelectorAll('.mega-menu__level2-panel').forEach((p) => {
        p.hidden = true;
        p.style.display = 'none';
      });
    }

    function hideAllL3Panels() {
      level3ColumnContent.querySelectorAll('.mega-menu__level3-panel').forEach((p) => {
        p.hidden = true;
        p.style.display = 'none';
      });
    }

    function setL2HeaderFromL1(l1Link) {
      const t = l1Link?.querySelector('.mega-menu__link-title');
      if (level2ColumnHeader) level2ColumnHeader.textContent = t ? t.textContent.trim() : '';
    }

    function setL3HeaderFromL2(l2Link) {
      const t = l2Link?.querySelector('.mega-menu__link-title');
      if (level3ColumnHeader) level3ColumnHeader.textContent = t ? t.textContent.trim() : '';
    }

    function showL2Panel(shopifyIndex) {
      hideAllL2Panels();
      hideAllL3Panels();
      level3Column.style.display = 'none';

      const panel = level2ColumnContent.querySelector('.mega-menu__level2-panel[data-panel-for-l1="' + shopifyIndex + '"]');
      const hasLinks = panel && panel.querySelector('a.menu-level-2-link');
      if (!hasLinks) {
        level2Column.style.display = 'none';
        return;
      }
      panel.hidden = false;
      panel.style.display = 'flex';
      panel.style.flexDirection = 'column';
      level2Column.style.display = 'flex';
    }

    function showL3Panel(l1Index, l2Index) {
      hideAllL3Panels();
      const panel = level3ColumnContent.querySelector(
        '.mega-menu__level3-panel[data-l1-index="' + l1Index + '"][data-l2-index="' + l2Index + '"]'
      );
      if (!panel || !panel.querySelector('a.mega-menu__link--level-3')) {
        level3Column.style.display = 'none';
        return;
      }
      panel.hidden = false;
      panel.style.display = 'flex';
      panel.style.flexDirection = 'column';
      level3Column.style.display = 'flex';
    }

    function clearL2Active() {
      level2ColumnContent.querySelectorAll('.menu-level-2-link').forEach((l) => {
        l.classList.remove('active');
        l.removeAttribute('aria-expanded');
      });
    }

    function activateLevel1(shopifyIndex, l1Link) {
      level1Links.forEach((l) => {
        l.classList.remove('active');
        l.removeAttribute('aria-expanded');
      });
      clearL2Active();
      hideAllL3Panels();
      level3Column.style.display = 'none';
      setL2HeaderFromL1(l1Link);
      showL2Panel(shopifyIndex);
      l1Link.setAttribute('aria-expanded', 'true');
      l1Link.classList.add('active');
    }

    function wireLevel2Links() {
      container.querySelectorAll('.menu-level-2-link[data-has-children="true"]').forEach((l2) => {
        l2.addEventListener('mouseenter', function () {
          const l1Index = l2.getAttribute('data-l1-index');
          const l2Index = l2.getAttribute('data-l2-index');
          if (!l1Index || !l2Index) return;

          if (!(mqDesktop.matches && mqHover.matches)) {
            setL3HeaderFromL2(l2);
            showL3Panel(l1Index, l2Index);
            clearL2Active();
            l2.classList.add('active');
            l2.setAttribute('aria-expanded', 'true');
            return;
          }

          l2.dataset.aimTarget = 'level3';
          const currentTimerId = ++activationTimerId;
          if (activationTimer) clearTimeout(activationTimer);

          const delay = menuAim.delayFor(l2);
          activationTimer = setTimeout(() => {
            if (currentTimerId !== activationTimerId) return;
            setL3HeaderFromL2(l2);
            showL3Panel(l1Index, l2Index);
            clearL2Active();
            l2.classList.add('active');
            l2.setAttribute('aria-expanded', 'true');
          }, delay);
        });

        l2.addEventListener('focus', function () {
          if (!isLinkKeyboardFocused(l2)) return;

          const l1Index = l2.getAttribute('data-l1-index');
          const l2Index = l2.getAttribute('data-l2-index');
          if (!l1Index || !l2Index) return;
          clearTimeout(activationTimer);
          setL3HeaderFromL2(l2);
          showL3Panel(l1Index, l2Index);
          clearL2Active();
          l2.classList.add('active');
          l2.setAttribute('aria-expanded', 'true');
        });
      });
    }

    wireLevel2Links();

    level3Column.addEventListener('mouseenter', () => {
      if (activationTimer) {
        clearTimeout(activationTimer);
        activationTimer = null;
      }
    });

    level1Links.forEach((link) => {
      const shopifyIndex = parseInt(link.getAttribute('data-level-1-index'), 10);
      if (Number.isNaN(shopifyIndex)) return;

      const hasSubmenu = link.dataset.hasChildren === 'true';

      link.addEventListener('mouseenter', function () {
        if (!hasSubmenu) return;

        if (!(mqDesktop.matches && mqHover.matches)) {
          activateLevel1(shopifyIndex, link);
          return;
        }
        clearTimeout(activationTimer);
        link.dataset.aimTarget = 'level2';
        const delay = menuAim.delayFor(link);
        activationTimer = setTimeout(() => {
          activateLevel1(shopifyIndex, link);
        }, delay);
      });

      link.addEventListener('focus', function () {
        if (!hasSubmenu) return;
        if (!isLinkKeyboardFocused(link)) return;

        clearTimeout(activationTimer);
        activateLevel1(shopifyIndex, link);
      });
    });

    container.addEventListener('mouseenter', startTrackingIfDesktop);
    container.addEventListener('mouseleave', function () {
      clearTimeout(activationTimer);
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => {
        level2Column.style.display = 'none';
        level3Column.style.display = 'none';
        hideAllL2Panels();
        hideAllL3Panels();
        level1Links.forEach((l) => {
          l.removeAttribute('aria-expanded');
          l.classList.remove('active');
        });
        clearL2Active();
        stopTracking();
      }, 250);
    });

    mqDesktop.addEventListener?.('change', () => {
      if (!(mqDesktop.matches && mqHover.matches)) stopTracking();
    });
    mqHover.addEventListener?.('change', () => {
      if (!(mqDesktop.matches && mqHover.matches)) stopTracking();
    });
  } catch (e) {
    console.error('Mega menu three-column error:', e);
  }
}

function initAllMegaMenuThreeColumns() {
  document.querySelectorAll('[data-mega-menu-three-column]').forEach(initMegaMenuThreeColumn);
}

window.addEventListener('load', initAllMegaMenuThreeColumns);
